import { createPortal } from 'react-dom';
import { Loader2 } from 'lucide-react';

const CTA_GRADIENT =
  'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)';

type CrossAgencyCompanyModalProps = {
  open: boolean;
  companyName: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onContinue: () => void;
};

export function CrossAgencyCompanyModal({
  open,
  companyName,
  isSubmitting,
  onCancel,
  onContinue,
}: CrossAgencyCompanyModalProps) {
  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onCancel}
      />
      <div className="camp-fade-up relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="m-0 text-lg font-semibold text-[var(--dash-ink)]">
          Company already registered
        </h2>
        <p className="mt-2 mb-0 text-sm leading-relaxed text-[var(--dash-muted)]">
          <span className="font-medium text-[var(--dash-ink)]">
            {companyName || 'This company'}
          </span>{' '}
          is already registered with another agency on Screenista.
        </p>
        <p className="mt-2 mb-0 text-sm leading-relaxed text-[var(--dash-muted)]">
          If you want to continue, this client will be created as{' '}
          <span className="font-medium text-[var(--dash-ink)]">Not verified</span>{' '}
          because the company is already active with another agency. Admin
          approval will be required before it can be used for campaigns.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="h-10 rounded-xl px-4 text-sm font-medium text-[var(--dash-muted)] hover:bg-[var(--dash-soft)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={onContinue}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: CTA_GRADIENT }}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Continue for admin approval
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
